/**
 * BulkActionsBar Component
 *
 * Displays bulk action buttons for selected users
 * Includes: Activate, Deactivate, Delete, Change Role
 */

import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { User } from '../../pages/PlatformUsers';
import { getAvailableRoles } from '../../utils/roleMapping';

interface BulkActionsBarProps {
  selectedUserIds: Set<string>;
  users: User[];
  onClearSelection: () => void;
  onBulkActivate: (userIds: string[]) => Promise<void>;
  onBulkDeactivate: (userIds: string[]) => Promise<void>;
  onBulkDelete: (userIds: string[]) => Promise<void>;
  onBulkRoleChange: (userIds: string[], newRole: string) => Promise<void>;
}

export const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedUserIds,
  users,
  onClearSelection,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  onBulkRoleChange
}) => {
  const shouldReduceMotion = useReducedMotion();
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const selectedUsers = users.filter(u => selectedUserIds.has(u.id!));
  const activeCount = selectedUsers.filter(u => u.isActive).length;
  const inactiveCount = selectedUsers.filter(u => !u.isActive).length;

  // Slide-in animation variants
  const barVariants = shouldReduceMotion
    ? undefined
    : {
        initial: { opacity: 0, y: 50 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 50 }
      };

  const menuVariants = shouldReduceMotion
    ? undefined
    : {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 }
      };

  const handleBulkAction = async (
    action: () => Promise<void>,
    actionName: string
  ) => {
    setIsProcessing(true);
    try {
      await action();
    } catch (error) {
      console.error(`Bulk ${actionName} failed:`, error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      variants={barVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 flex items-center justify-between shadow-md"
    >
      <div className="flex items-center space-x-4">
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="text-sm font-medium text-purple-900"
        >
          {selectedUserIds.size} user{selectedUserIds.size > 1 ? 's' : ''} selected
        </motion.span>
        <motion.button
          onClick={onClearSelection}
          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
          disabled={isProcessing}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Clear Selection
        </motion.button>
      </div>

      <div className="flex items-center space-x-2">
        {/* Bulk Activate */}
        <AnimatePresence>
          {inactiveCount > 0 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleBulkAction(
                () => onBulkActivate(Array.from(selectedUserIds)),
                'activate'
              )}
              disabled={isProcessing}
              className="inline-flex items-center px-3 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-white hover:bg-green-50 disabled:opacity-50"
              title={`Activate ${inactiveCount} inactive user${inactiveCount > 1 ? 's' : ''}`}
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              Activate ({inactiveCount})
            </motion.button>
          )}
        </AnimatePresence>

        {/* Bulk Deactivate */}
        <AnimatePresence>
          {activeCount > 0 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleBulkAction(
                () => onBulkDeactivate(Array.from(selectedUserIds)),
                'deactivate'
              )}
              disabled={isProcessing}
              className="inline-flex items-center px-3 py-2 border border-yellow-300 rounded-md text-sm font-medium text-yellow-700 bg-white hover:bg-yellow-50 disabled:opacity-50"
              title={`Deactivate ${activeCount} active user${activeCount > 1 ? 's' : ''}`}
            >
              <XCircleIcon className="h-4 w-4 mr-1" />
              Deactivate ({activeCount})
            </motion.button>
          )}
        </AnimatePresence>

        {/* Bulk Delete */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleBulkAction(
            () => onBulkDelete(Array.from(selectedUserIds)),
            'delete'
          )}
          disabled={isProcessing}
          className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
          title={`Delete ${selectedUserIds.size} user${selectedUserIds.size > 1 ? 's' : ''}`}
        >
          <TrashIcon className="h-4 w-4 mr-1" />
          Delete
        </motion.button>

        {/* Bulk Role Change */}
        <div className="relative">
          <motion.button
            onClick={() => setShowRoleMenu(!showRoleMenu)}
            disabled={isProcessing}
            className="inline-flex items-center px-3 py-2 border border-purple-300 rounded-md text-sm font-medium text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <UserGroupIcon className="h-4 w-4 mr-1" />
            Change Role
          </motion.button>

          <AnimatePresence>
            {showRoleMenu && (
              <motion.div
                variants={menuVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
              >
                <div className="py-1" role="menu">
                  {getAvailableRoles().map(({ value, label }) => (
                    <motion.button
                      key={value}
                      onClick={() => {
                        handleBulkAction(
                          () => onBulkRoleChange(Array.from(selectedUserIds), value),
                          'role change'
                        );
                        setShowRoleMenu(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                      role="menuitem"
                      whileHover={{ x: 4, backgroundColor: 'rgb(250 245 255)' }}
                    >
                      {label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center text-sm text-purple-600"
            >
              <motion.svg
                className="h-4 w-4 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </motion.svg>
              Processing...
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default BulkActionsBar;
